// app/src/lib/procurement/items.js — Item Master & VendorItem mapping (US-2.3).

import { db } from '../db.js';
import { requireUserId, normalize } from './util.js';
import { mutate } from '../scale/instrument.js';

/** Allowed categories from SPECIFICATION §4.1. */
export const CATEGORIES = Object.freeze([
  'produce', 'dairy', 'meat', 'seafood', 'dry-goods',
  'bakery', 'beverage', 'alcohol', 'cleaning', 'packaging',
  'equipment', 'other-cogs', 'other-opex',
]);

const CATEGORY_SET = new Set(CATEGORIES);

/** @typedef {{ itemId:string, userId:string, internalSku:string, name:string, normalizedName:string, description?:string, category:string, defaultUom:string, packSize?:string, isActive:number }} Item */
/** @typedef {{ vendorItemId:string, userId:string, vendorId:string, itemId:string, vendorSku?:string, vendorDescription:string, normalizedVendorDescription:string, packSize:string, uom:string, isActive:number }} VendorItem */

/** Create an internal Item. SKU auto-generated if not provided. */
export async function createItem(input) {
  const userId = await requireUserId();
  if (!input.name) throw new Error('item.create: name required');
  const category = CATEGORY_SET.has(input.category) ? input.category : 'other-cogs';
  /** @type {Item} */
  const item = {
    itemId: input.itemId || crypto.randomUUID(),
    userId,
    internalSku: input.internalSku || `KJ-${shortId()}`,
    name: input.name,
    normalizedName: normalize(input.name),
    description: input.description,
    category,
    defaultUom: input.defaultUom || 'each',
    packSize: input.packSize,
    isActive: 1,
  };
  return mutate(
    { entityType: 'item', entityId: item.itemId, action: 'create', after: item },
    async () => { await db.items.put(item); return item; },
  );
}

export async function updateItem(itemId, patch) {
  const userId = await requireUserId();
  const existing = await db.items.get(itemId);
  if (!existing || existing.userId !== userId) throw new Error('item.update: not found');
  const next = {
    ...existing,
    ...patch,
    normalizedName: patch.name ? normalize(patch.name) : existing.normalizedName,
    category: patch.category && CATEGORY_SET.has(patch.category) ? patch.category : existing.category,
  };
  return mutate(
    { entityType: 'item', entityId: itemId, action: 'update', before: existing, after: next },
    async () => { await db.items.put(next); return next; },
  );
}

export async function deactivateItem(itemId) {
  return updateItem(itemId, { isActive: 0 });
}

export async function listActive() {
  const userId = await requireUserId();
  return db.items.where('[userId+isActive]').equals([userId, 1]).toArray();
}

export async function listByCategory(category) {
  const userId = await requireUserId();
  return db.items.where('[userId+category]').equals([userId, category]).toArray();
}

/**
 * Merge `srcId` into `dstId`. Reassigns vendorItems, priceEntries, contracts,
 * lineItems, poLineItems, parItems. Deletes src.
 */
export async function mergeItems(srcId, dstId) {
  const userId = await requireUserId();
  if (srcId === dstId) return;
  const [src, dst] = await Promise.all([db.items.get(srcId), db.items.get(dstId)]);
  if (!src || src.userId !== userId) throw new Error('item.merge: src not found');
  if (!dst || dst.userId !== userId) throw new Error('item.merge: dst not found');

  await db.transaction('rw',
    db.items, db.vendorItems, db.priceEntries, db.vendorContracts,
    db.lineItems, db.poLineItems, db.parItems,
    async () => {
      const reassign = async (table, indexed) => {
        const rows = await table.where(indexed).equals(srcId).toArray();
        for (const r of rows) { r.itemId = dstId; await table.put(r); }
      };
      await Promise.all([
        reassign(db.vendorItems, 'itemId'),
        reassign(db.priceEntries, 'itemId'),
        reassign(db.vendorContracts, 'itemId'),
        reassign(db.lineItems, 'itemId'),
        reassign(db.poLineItems, 'itemId'),
        reassign(db.parItems, 'itemId'),
      ]);
      await db.items.delete(srcId);
    },
  );
}

// ── VendorItem ─────────────────────────────────────────────────────────────

/** Create or upsert a VendorItem mapping (vendor SKU/description → internal item). */
export async function upsertVendorItem(input) {
  const userId = await requireUserId();
  if (!input.vendorId || !input.itemId || !input.vendorDescription) {
    throw new Error('vendorItem: vendorId, itemId, vendorDescription required');
  }
  // Look for an existing mapping with the same normalized description.
  const norm = normalize(input.vendorDescription);
  const existing = await db.vendorItems
    .where('[vendorId+itemId]').equals([input.vendorId, input.itemId])
    .filter((vi) => vi.normalizedVendorDescription === norm)
    .first();
  if (existing) {
    const merged = {
      ...existing,
      vendorSku: input.vendorSku ?? existing.vendorSku,
      packSize: input.packSize ?? existing.packSize,
      uom: input.uom ?? existing.uom,
      isActive: 1,
    };
    await db.vendorItems.put(merged);
    return merged;
  }
  /** @type {VendorItem} */
  const vi = {
    vendorItemId: crypto.randomUUID(),
    userId,
    vendorId: input.vendorId,
    itemId: input.itemId,
    vendorSku: input.vendorSku,
    vendorDescription: input.vendorDescription,
    normalizedVendorDescription: norm,
    packSize: input.packSize || '',
    uom: input.uom || 'each',
    isActive: 1,
  };
  await db.vendorItems.put(vi);
  return vi;
}

export async function listVendorItemsFor(vendorId) {
  return db.vendorItems.where('[vendorId+isActive]').equals([vendorId, 1]).toArray();
}

export async function listVendorsCarryingItem(itemId) {
  return db.vendorItems.where('itemId').equals(itemId).toArray();
}

function shortId() {
  // 8-char base36; collision risk acceptable for per-user SKUs.
  return Math.random().toString(36).slice(2, 10);
}
